import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import { useAppSelector } from "../../hooks/usereduxHooks";
import IconifyIcon from "../icon/Icon";
import SendEmailModal from "./SendEmailModal";
import {
  extractReportDataFromElement,
  ExtractedReportData,
} from "./reportDataExtractor";

export interface PrintButtonProps {
  componentRef?: any;
  reportTitle?: string;
  reportType?: string;
  filters?: {
    dateFrom?: string;
    dateTo?: string;
    partyName?: string;
    voucherNumber?: string;
    slipType?: string;
    searchKeyword?: string;
  };
  summaryItems?: Array<{ label: string; value: string }>;
}

export default function PrintButton({
  componentRef,
  reportTitle,
  reportType,
  filters,
  summaryItems,
}: PrintButtonProps) {
  const {
    user: { email, name },
  } = useAppSelector((store) => store.User);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedReportData>({
    reportTitle: reportTitle || "Account Report",
    columns: [],
    rows: [],
    summaryItems: [],
  });

  const handleOpenEmailModal = () => {
    const data = extractReportDataFromElement(
      componentRef?.current,
      reportTitle || "Account Report",
    );
    if (reportTitle) data.reportTitle = reportTitle;
    if (reportType) data.reportType = reportType;
    if (filters) data.filters = { ...data.filters, ...filters };
    if (summaryItems && summaryItems.length > 0) {
      data.summaryItems = [...data.summaryItems, ...summaryItems];
    }
    setExtractedData(data);
    setEmailModalOpen(true);
  };

  const handlePrint = useReactToPrint({
    content: () => {
      if (componentRef?.current) {
        const contentToPrint = (componentRef.current as any).cloneNode(true); // Clone the element

        const elementsToRemove = contentToPrint.querySelectorAll(
          ".itemCode, .Code, .receipt, .slip",
        ); // Select elements to remove

        const widthToAdd = contentToPrint.querySelectorAll(".width_zero");
        widthToAdd?.forEach((element: any) => {
          element.style.width = "100px";
        });

        const displayShouldFlex = contentToPrint.querySelectorAll(
          ".total_span, .bill-rate",
        );

        displayShouldFlex?.forEach((element: any) => {
          element.style.display = "contents";
          element.style.width = "100%";
        });

        elementsToRemove.forEach(
          (element: { parentNode: { removeChild: (arg0: any) => any } }) =>
            element.parentNode.removeChild(element),
        );

        // Remove empty columns
        const tables = contentToPrint.querySelectorAll("table");
        tables.forEach((table: any) => {
          const rows = table.querySelectorAll("tr");
          if (rows.length === 0) return;

          // Get the number of columns from the first row
          const firstRow = rows[0];
          const totalCols = firstRow.cells.length;
          const columnsToRemove: number[] = [];

          // Check each column to see if it's empty
          for (let colIndex = 0; colIndex < totalCols; colIndex++) {
            let isEmpty = true;

            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
              const cell = rows[rowIndex].cells[colIndex];
              if (cell && cell.textContent && cell.textContent.trim() !== "") {
                isEmpty = false;
                break;
              }
            }

            if (isEmpty) {
              columnsToRemove.push(colIndex);
            }
          }

          // Remove empty columns (in reverse order to maintain indices)
          columnsToRemove.reverse().forEach((colIndex) => {
            rows.forEach((row: any) => {
              if (row.cells[colIndex]) {
                row.deleteCell(colIndex);
              }
            });
          });

          // Add a data attribute to indicate number of columns for styling
          const remainingCols = totalCols - columnsToRemove.length;
          table.setAttribute("data-cols", remainingCols.toString());
        });

        // Add print-specific styles
        const printStyles = document.createElement("style");
        printStyles.innerHTML = `
          @media print {
            * {
              font-size: 10px !important;
              line-height: 1.2 !important;
            }
            
            table {
              border-collapse: collapse !important;
              width: 100% !important;
              font-size: 9px !important;
            }
            
            th, td {
              padding: 2px 4px !important;
              border: 1px solid #333 !important;
              font-size: 9px !important;
              text-align: left !important;
            }
            
            th {
              background-color: #f5f5f5 !important;
              font-weight: bold !important;
            }
            
            .top-content {
              margin-bottom: 10px !important;
              font-size: 10px !important;
            }
            
            .top-bar {
              display: flex !important;
              justify-content: space-between !important;
              align-items: center !important;
              padding: 5px 0 !important;
              border-bottom: 2px solid #333 !important;
              margin-bottom: 10px !important;
            }
            
            .shop-name, .account-name, .date {
              font-size: 10px !important;
              font-weight: bold !important;
            }
            
            .shop-name span, .account-name span, .date span {
              font-weight: normal !important;
            }
            
            /* Ensure all table elements have borders */
            table, thead, tbody, tfoot, tr, td, th {
              border: 1px solid #333 !important;
            }
            
            /* Remove any existing margins/padding that might interfere */
            body {
              margin: 0 !important;
              padding: 10px !important;
            }
            
            /* Make sure the content fits well on the page */
            .MuiTableContainer-root {
              overflow: visible !important;
            }
            
            /* Style for better readability */
            tbody tr:nth-child(even) {
              background-color: #f9f9f9 !important;
            }
            
            /* Adjust specific column widths based on remaining columns */
            th:first-child, td:first-child {
              width: 40% !important;
            }
            
            th:nth-child(2), td:nth-child(2) {
              width: 30% !important;
            }
            
            th:nth-child(3), td:nth-child(3) {
              width: 20% !important;
              text-align: right !important;
            }
            
            th:nth-child(4), td:nth-child(4) {
              width: 10% !important;
              text-align: center !important;
            }
            
            /* If there are only 3 columns, adjust accordingly */
            table[data-cols="3"] th:first-child, 
            table[data-cols="3"] td:first-child {
              width: 50% !important;
            }
            
            table[data-cols="3"] th:nth-child(2), 
            table[data-cols="3"] td:nth-child(2) {
              width: 30% !important;
            }
            
            table[data-cols="3"] th:nth-child(3), 
            table[data-cols="3"] td:nth-child(3) {
              width: 20% !important;
              text-align: right !important;
            }
          }
        `;

        // Insert styles into the cloned content
        contentToPrint.appendChild(printStyles);

        const topContent = document.createElement("div");
        topContent.classList.add("top-content"); // Add a class for styling (optional)
        topContent.innerHTML = `
    <div class="top-bar">
      <div class="shop-name">Shop Name: <span>${name}</span></div>
      <div class="account-name">Account: <span>${email}</span></div>
      <div class="date">Date: <span id="current-date">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</span></div>
    </div>
    `;

        // Insert the new content on top of the cloned content
        contentToPrint.insertBefore(topContent, contentToPrint.firstChild);

        return contentToPrint!;
      }
    },
    // Optional: Add page style configuration
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      
      @media print {
        body {
          font-family: Arial, sans-serif;
          color: #000;
        }
      }
    `,
  });

  return (
    <Box display={"flex"} alignItems="center" gap={1} flexWrap="wrap">
      <Button
        size="small"
        onClick={() => handlePrint && handlePrint()}
        variant="contained"
      >
        Print all
      </Button>
      <Button
        size="small"
        onClick={handleOpenEmailModal}
        variant="outlined"
        startIcon={<IconifyIcon icon="mdi:email-send-outline" />}
      >
        Send email
      </Button>
      <SendEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        reportData={extractedData}
      />
    </Box>
  );
}
